import React, { useId } from 'react';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

export interface FormFieldMessageProps extends Styleable {
    hint?: React.ReactNode;
    error?: React.ReactNode;
    id?: string;
}

/**
 * The hint-or-error line by itself, split out of `FormField` for controls
 * that are not a single labelled input — `AppUriListField`'s multi-row
 * `<fieldset>` is the reason this exists rather than staying private:
 * same "error replaces the hint, never stacks under it" rule, same 12px
 * sheet typography, without forcing a fieldset into a `<label>` wrapper it
 * does not want.
 */
export function FormFieldMessage({
    hint,
    error,
    id,
    className,
    style,
}: FormFieldMessageProps): React.ReactElement | null {
    const colors = useUIColors();
    const message = error ?? hint;
    if (!message) return null;
    return (
        <span
            id={id}
            className={cx('ui-form-field-message', className)}
            style={{ fontSize: 12, lineHeight: 1.5, color: error ? colors.errorText : colors.textMuted, ...style }}
        >
            {message}
        </span>
    );
}

export interface FormFieldProps extends Styleable {
    label: React.ReactNode;
    /** Marks the label with the design's own required-field glyph (a red
     *  trailing asterisk) — purely presentational, does not add HTML
     *  `required` to the control; the caller's own input still owns that. */
    required?: boolean;
    /** Standing guidance for the field, shown when `error` is absent. */
    hint?: React.ReactNode;
    /**
     * Validation failure for this field. Takes over the hint's spot rather
     * than stacking below it — the sheet's own "Lỗi" field has exactly one
     * line under the control, never a hint AND an error at once. A caller
     * that still wants both visible has a real reason to say so explicitly
     * (`hint` overlaid with its own error node), not a default this
     * component should produce.
     */
    error?: React.ReactNode;
    /** The control itself — `Input`/`Select`/`TextArea`/anything else. */
    children: React.ReactNode;
}

/**
 * EAA-1846 (design's "Input · Label · Select" component sheet): the
 * label-above-control field shape every form in this app hand-rolls —
 * a raw `<label>` wrapping the control, then a separate hint `<p>` and,
 * some of the time, a separate error `<p>` stacked below it. Both problems
 * this fixes are real, not stylistic: (1) that markup pulls its 6px
 * label-to-control gap from a GLOBAL class (`.portal-admin-form label`),
 * so it drifts the moment any other page's CSS touches that selector,
 * and (2) the hint-then-error stacking shows BOTH lines at once in an
 * error state, taking visibly more vertical space than the design ever
 * draws.
 *
 * Sizes/colors are the sheet's own literal values (13px/500 label, 6px
 * gap, 12px hint/error) via inline styles, not global classes — this
 * package ships no CSS, and a field's spacing should not depend on
 * which page's stylesheet happened to load.
 */
export function FormField({
    label,
    required = false,
    hint,
    error,
    children,
    className,
    style,
}: FormFieldProps): React.ReactElement {
    const colors = useUIColors();
    const messageId = useId();
    const hasMessage = Boolean(error ?? hint);

    return (
        <label
            className={cx('ui-form-field', className)}
            style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}
        >
            <span style={{ fontSize: 13, fontWeight: 500, color: colors.textSecondary }}>
                {label}
                {required && <span style={{ color: colors.error }}> *</span>}
            </span>
            {React.isValidElement(children)
                ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
                      'aria-describedby': hasMessage ? messageId : undefined,
                      'aria-invalid': error ? true : undefined,
                  })
                : children}
            <FormFieldMessage hint={hint} error={error} id={messageId} />
        </label>
    );
}

export default FormField;
